import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "result", "resultValue", "resultStatus", "resultUnit", "interpretation", "unitLabel", "unitToggle"]
  static values = {
    type: String,
    unit: { type: String, default: "mgdl" }
  }

  // Conversion factors (same as your Rails model)
  static CHOLESTEROL_FACTOR = 38.67  // 1 mmol/L = 38.67 mg/dL
  static TRIGLYCERIDE_FACTOR = 88.57 // 1 mmol/L = 88.57 mg/dL

  connect() {
    this.calculate()
  }

  // Validation ranges (in mg/dL)
  static RANGES = {
    total_cholesterol: { min: 50, max: 500 },
    hdl: { min: 10, max: 150 },
    ldl: { min: 20, max: 400 },
    triglycerides: { min: 20, max: 2000 }
  }

  calculate() {
    // Get raw input values
    const rawInputs = {}
    let hasInvalidInput = false

    this.inputTargets.forEach(input => {
      const value = parseFloat(input.value)

      // Check for empty, negative, or zero values
      if (!input.value || isNaN(value) || value <= 0) {
        rawInputs[input.name] = 0
        return
      }

      rawInputs[input.name] = value
    })

    // Convert to mg/dL if user entered mmol/L (calculations use mg/dL internally)
    const inputs = this.convertToMgdl(rawInputs)

    // Validate ranges (after conversion to mg/dL)
    for (const [key, value] of Object.entries(inputs)) {
      if (value === 0) continue
      const range = this.constructor.RANGES[key]
      if (range && (value < range.min || value > range.max)) {
        hasInvalidInput = true
        break
      }
    }

    if (hasInvalidInput) {
      this.displayError("Value out of expected range. Please check your inputs.")
      return
    }

    let result, status, interpretation

    switch (this.typeValue) {
      case "non-hdl":
        result = this.calculateNonHDL(inputs)
        ;({ status, interpretation } = this.interpretNonHDL(result))
        break
      case "tg-hdl-ratio":
        result = this.calculateTgHdlRatio(inputs)
        ;({ status, interpretation } = this.interpretTgHdlRatio(result))
        break
      case "ldl":
        result = this.calculateLDL(inputs)
        ;({ status, interpretation } = this.interpretLDL(result))
        break
      case "cholesterol-ratio":
        result = this.calculateCholesterolRatio(inputs)
        ;({ status, interpretation } = this.interpretCholesterolRatio(result))
        break
      default:
        return
    }

    this.displayResult(result, status, interpretation)
  }

  toggleUnit(event) {
    this.unitValue = event.target.value
    this.updateUnitLabels()
    this.calculate()
  }

  updateUnitLabels() {
    const unit = this.unitValue === "mmol" ? "mmol/L" : "mg/dL"
    this.unitLabelTargets.forEach(label => {
      label.textContent = unit
    })

    // Update input placeholders with typical values for the selected unit
    const placeholders = this.unitValue === "mmol" ? {
      total_cholesterol: "5.2",
      hdl: "1.3",
      ldl: "2.6",
      triglycerides: "1.7"
    } : {
      total_cholesterol: "200",
      hdl: "50",
      ldl: "100",
      triglycerides: "150"
    }

    this.inputTargets.forEach(input => {
      if (placeholders[input.name]) {
        input.placeholder = placeholders[input.name]
      }
    })
  }

  // Convert input values from selected unit to mg/dL for calculations
  convertToMgdl(inputs) {
    if (this.unitValue === "mgdl") return inputs

    const converted = {}
    for (const [key, value] of Object.entries(inputs)) {
      if (!value) {
        converted[key] = 0
        continue
      }

      // Triglycerides use different conversion factor
      if (key === "triglycerides") {
        converted[key] = value * this.constructor.TRIGLYCERIDE_FACTOR
      } else {
        converted[key] = value * this.constructor.CHOLESTEROL_FACTOR
      }
    }
    return converted
  }

  // Convert result from mg/dL to selected unit for display
  convertFromMgdl(value, isTriglyceride = false) {
    if (this.unitValue === "mgdl" || value === null) return value

    const factor = isTriglyceride
      ? this.constructor.TRIGLYCERIDE_FACTOR
      : this.constructor.CHOLESTEROL_FACTOR
    return value / factor
  }

  // Non-HDL = Total Cholesterol - HDL
  calculateNonHDL(inputs) {
    const { total_cholesterol, hdl } = inputs
    if (!total_cholesterol || !hdl) return null
    const result = total_cholesterol - hdl
    // HDL cannot be greater than total cholesterol
    if (result < 0) return null
    return result
  }

  interpretNonHDL(value) {
    if (value === null) return { status: null, interpretation: "Enter values above to calculate" }

    // mg/dL ranges
    if (value < 130) {
      return { status: "optimal", interpretation: "Optimal - Low cardiovascular risk" }
    } else if (value < 160) {
      return { status: "near-optimal", interpretation: "Near optimal - Moderate risk" }
    } else if (value < 190) {
      return { status: "borderline", interpretation: "Borderline high - Consider lifestyle changes" }
    } else if (value < 220) {
      return { status: "high", interpretation: "High - Talk to your doctor about treatment" }
    } else {
      return { status: "very-high", interpretation: "Very high - Medical attention recommended" }
    }
  }

  // TG:HDL Ratio = Triglycerides / HDL
  calculateTgHdlRatio(inputs) {
    const { triglycerides, hdl } = inputs
    if (!triglycerides || !hdl) return null
    return triglycerides / hdl
  }

  interpretTgHdlRatio(value) {
    if (value === null) return { status: null, interpretation: "Enter values above to calculate" }

    // Ratio interpretation (mg/dL based)
    if (value < 2) {
      return { status: "optimal", interpretation: "Optimal - Low insulin resistance risk" }
    } else if (value < 3) {
      return { status: "near-optimal", interpretation: "Acceptable - Moderate metabolic health" }
    } else if (value < 4) {
      return { status: "borderline", interpretation: "Borderline - May indicate insulin resistance" }
    } else {
      return { status: "high", interpretation: "High - Strong indicator of insulin resistance" }
    }
  }

  // LDL (Friedewald) = Total Cholesterol - HDL - (Triglycerides / 5)
  calculateLDL(inputs) {
    const { total_cholesterol, hdl, triglycerides } = inputs
    if (!total_cholesterol || !hdl || !triglycerides) return null
    if (triglycerides > 400) return null // Friedewald not accurate above 400
    const result = total_cholesterol - hdl - (triglycerides / 5)
    // Result should not be negative
    if (result < 0) return null
    return result
  }

  interpretLDL(value) {
    if (value === null) {
      const tgLimit = this.unitValue === "mmol" ? "4.5 mmol/L" : "400 mg/dL"
      return { status: null, interpretation: `Enter values (Friedewald formula not accurate if TG > ${tgLimit})` }
    }

    // mg/dL ranges (ATP III guidelines)
    if (value < 100) {
      return { status: "optimal", interpretation: "Optimal" }
    } else if (value < 130) {
      return { status: "near-optimal", interpretation: "Near optimal / Above optimal" }
    } else if (value < 160) {
      return { status: "borderline", interpretation: "Borderline high" }
    } else if (value < 190) {
      return { status: "high", interpretation: "High" }
    } else {
      return { status: "very-high", interpretation: "Very high" }
    }
  }

  // TC:HDL Ratio = Total Cholesterol / HDL
  calculateCholesterolRatio(inputs) {
    const { total_cholesterol, hdl } = inputs
    if (!total_cholesterol || !hdl) return null
    return total_cholesterol / hdl
  }

  interpretCholesterolRatio(value) {
    if (value === null) return { status: null, interpretation: "Enter values above to calculate" }

    if (value < 3.5) {
      return { status: "optimal", interpretation: "Optimal - Low cardiovascular risk" }
    } else if (value < 5) {
      return { status: "near-optimal", interpretation: "Acceptable - Average risk" }
    } else if (value < 6) {
      return { status: "borderline", interpretation: "Borderline - Above average risk" }
    } else {
      return { status: "high", interpretation: "High - Increased cardiovascular risk" }
    }
  }

  displayError(message) {
    if (!this.hasResultTarget) return

    this.resultTarget.classList.remove("hidden")

    if (this.hasResultValueTarget) {
      this.resultValueTarget.textContent = "—"
    }

    if (this.hasResultStatusTarget) {
      this.resultStatusTarget.className = "inline-flex px-3 py-1 rounded-full text-sm font-medium bg-slate-100 text-slate-600"
      this.resultStatusTarget.textContent = "Invalid"
    }

    if (this.hasInterpretationTarget) {
      this.interpretationTarget.textContent = message
    }
  }

  displayResult(value, status, interpretation) {
    if (!this.hasResultTarget) return

    if (value === null) {
      this.resultTarget.classList.add("hidden")
      return
    }

    this.resultTarget.classList.remove("hidden")

    if (this.hasResultValueTarget) {
      // Convert result for display if needed (ratios don't need conversion)
      const isRatio = ["tg-hdl-ratio", "cholesterol-ratio"].includes(this.typeValue)
      const displayValue = isRatio ? value : this.convertFromMgdl(value, false)
      this.resultValueTarget.textContent = displayValue.toFixed(1)
    }

    // Update result unit label if present
    if (this.hasResultUnitTarget) {
      const isRatio = ["tg-hdl-ratio", "cholesterol-ratio"].includes(this.typeValue)
      if (!isRatio) {
        this.resultUnitTarget.textContent = this.unitValue === "mmol" ? "mmol/L" : "mg/dL"
      }
    }

    if (this.hasResultStatusTarget && status) {
      const statusClasses = {
        "optimal": "bg-emerald-100 text-emerald-800",
        "near-optimal": "bg-cyan-100 text-cyan-800",
        "borderline": "bg-amber-100 text-amber-800",
        "high": "bg-orange-100 text-orange-800",
        "very-high": "bg-rose-100 text-rose-800"
      }

      this.resultStatusTarget.className = `inline-flex px-3 py-1 rounded-full text-sm font-medium ${statusClasses[status] || ""}`
      this.resultStatusTarget.textContent = status.replace("-", " ").replace(/\b\w/g, l => l.toUpperCase())
    }

    if (this.hasInterpretationTarget && interpretation) {
      this.interpretationTarget.textContent = interpretation
    }
  }
}
